// node modules
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
// modules
import FetchService from 'modules/Fetch/service';
import LiveDataService from 'modules/LiveData';
import SearchService from 'modules/Search';
import FormatService from 'modules/Format';
import HoldingCalculations from 'modules/TradesPW3/Holdings/HoldingCalculations';
import { MARKET_FEEDER_MODES } from 'modules/LiveData/constants';
import { SegmentType } from 'modules/Search/constants';
import { LStorage } from 'modules/Storage';
// typings
import { Holding, HoldingFromBE, HoldingsData, Instrument } from './typings';
// store
import HoldingsStore from './store';

class HoldingsService extends HoldingsStore {
  getHoldings = async () => {
    const response = await FetchService.request<'PORTFOLIO', HoldingsData>(
      'PORTFOLIO',
      '/holdings',
      'GET_HOLDINGS',
    );

    if (!response.error) {
      if (!isEmpty(response?.data?.active)) {
        await this.handleGetHoldings(response?.data?.active || []);
      }
    }
  };

  public updateHoldings = (updatedHolding: HoldingFromBE): Holding | void => {
    const [extendedHolding] = this.getSplittedHoldings([updatedHolding]);

    if (!extendedHolding?.id) {
      return;
    }

    const indexOfUpdatedHolding = this.holdings.findIndex(
      ({ id }) => extendedHolding.id === id,
    );

    let fullHoldingData = extendedHolding;

    if (indexOfUpdatedHolding >= 0) {
      const updatedHoldings = this.holdings.reduce<Holding[]>(
        (acc, holding, index) => {
          if (index === indexOfUpdatedHolding) {
            fullHoldingData = {
              ...this.holdings[indexOfUpdatedHolding],
              ...extendedHolding,
            };

            return [...acc, fullHoldingData];
          }
          return [...acc, holding];
        },
        [],
      );

      this.setHoldings(updatedHoldings);
    } else {
      const updatedHoldings = [extendedHolding, ...this.holdings];
      this.setHoldings(updatedHoldings);
    }

    return fullHoldingData;
  };

  getHoldingExchangeTokenById = (id: string) => id.split('-')[0];

  private handleGetHoldings = async (holdings: HoldingFromBE[]) => {
    const splittedHoldings = this.getSplittedHoldings(holdings);
    const modifiedHoldings = await this.modifyHoldings(splittedHoldings);

    this.subscribeHoldings(modifiedHoldings);

    this.setHoldings(modifiedHoldings);
  };

  private modifyHoldings =
    async (holdingsList: Holding[]): Promise<Holding[]> =>
      Promise.all(holdingsList.map(async holding => {
        const token_nse = holding?.token_nse;
        const token_bse = holding?.token_bse;

        const exchangeTokenNSE = `${SegmentType.NSE_EQ}|${token_nse}`;
        const scripDataNSE =
          await SearchService.searchByExchangeToken(exchangeTokenNSE || '');

        const tvExchangeTokenNSE = FormatService.generateCustomTvExchangeToken(
          scripDataNSE?.displayName || '',
          exchangeTokenNSE || '',
        );

        let scripDataBSE = null;
        let tvExchangeTokenBSE = '';

        if (token_bse) {
          const exchangeTokenBSE = `${SegmentType.BSE_EQ}|${token_bse}`;

          scripDataBSE = await SearchService.searchByExchangeToken(
            exchangeTokenBSE || '',
          );

          tvExchangeTokenBSE = FormatService.generateCustomTvExchangeToken(
            scripDataBSE?.displayName || '',
            exchangeTokenBSE || '',
          );
        }

        return {
          ...holding,
          symbol: scripDataNSE?.symbol || scripDataBSE?.symbol || '',
          tvExchangeTokenNSE,
          tvExchangeTokenBSE,
          instrumentKeyNSE: scripDataNSE?.instrumentKey,
          instrumentKeyBSE: scripDataBSE?.instrumentKey,
        };
      }));

  private getSplittedHoldings = (holdings: HoldingFromBE[]): Holding[] => {
    const splittedHoldings: Holding[] = holdings.reduce<Holding[]>(
      (res, holding) => {
        const token_nse = this.getToken(holding.instrument, SegmentType.NSE_EQ);
        const token_bse = this.getToken(holding.instrument, SegmentType.BSE_EQ);

        const symbol = holding.instrument?.[0]?.s;

        const exchangeToken =
          (token_bse && token_nse) || (token_nse && !token_bse)
            ? `${SegmentType.NSE_EQ}|${token_nse}`
            : `${SegmentType.BSE_EQ}|${token_bse}`;

          if (holding.fillInfo?.demat?.qty || holding.fillInfo?.t1?.qty) {
          res.push({
            ...holding,
            id: `${exchangeToken}`,
            netInfo: holding.netInfoV3,
            token_nse,
            token_bse,
            symbol,
          });
}

        return res;
      },
      [],
    );

    return splittedHoldings;
  };

  /**
   * Subscribes to each holding for NSE and BSE exchange
   * @param  {HoldingFromBE[]} holdings array of holdings
   */
  private subscribeHoldings = (holdings: Holding[]) => {
    const tokens =
      holdings.reduce((acc, { instrumentKeyNSE, instrumentKeyBSE }) => {
        if (instrumentKeyNSE) acc.push(instrumentKeyNSE);
        if (instrumentKeyBSE) acc.push(instrumentKeyBSE);

        return acc;
      }, [] as string[]);

    if (tokens.length) {
      LiveDataService.subscribeScrip(tokens, MARKET_FEEDER_MODES.LTPC);
    }
  };

  /**
   * Gets needed data for holding details in Portfolio tab
   * @param  {string} token exchange token of selected holding
   */
  generateHoldingPortfolioDetails = (holding: Holding) => {
    const { ltp } = HoldingCalculations.getHoldingLiveData(holding);

    if (holding) {
      const needToCalculateData = holding && !!ltp;

      // Total qty = Quantity + T1 holdings qty
      const totalQnty =
        (holding.fillInfo?.demat?.qty ?? 0) + (holding.fillInfo?.t1?.qty ?? 0);

      // Used qty = CNC used qty
      const usedQnty = holding.additionalInfo?.cnc_used_quantity ?? 0;

      // Net qty = Total qty - Used qty
      const netQnty = this.calculateNetQuantity(holding);

      // Current value = Net qty * LTP
      const currentValue = needToCalculateData ? netQnty * Number(ltp) : 0;

      // Investment = Net qty * avg price
      const investment = needToCalculateData
        ? netQnty * holding.fillInfo?.demat?.avgPrice ?? 0
        : 0;

      // PnL = (LTP - Avg price) * Net qty
      const overall = needToCalculateData
        ? (Number(ltp) - holding.fillInfo?.demat?.avgPrice ?? 0) * netQnty
        : 0;

      const overallPercentageValue = overall / investment;

      const overallPercentage = Number.isNaN(overallPercentageValue)
        ? 0
        : overallPercentageValue * 100;

      return {
        totalQnty,
        usedQnty,
        netQnty,
        investment: investment.toFixed(2),
        overall: overall.toFixed(2),
        overallPercentage: overallPercentage ? overallPercentage.toFixed(2) : '-',
        currentValue: currentValue ? currentValue.toFixed(2) : '-',
      };
    }
  };

  calculateNetQuantity = (holding: Holding) => {
    const { fillInfo } = holding || {};
    return fillInfo?.t1?.qty + fillInfo?.demat?.qty;
  };

  private getToken = (
    instruments: Instrument[],
    segment: SegmentType,
  ): number => {
    const filteredData = instruments.filter(
      instrument => instrument.e === segment,
    );

    return filteredData ? filteredData[0]?.t : 0;
  };

  setHoldingsDiscrepancyAlertReminder = () => {
    // Get the current date.
    const currentDate = dayjs();

    // Get the next Monday.
    const nextMonday = currentDate
      .add(1, 'week')
      .startOf('week')
      .add(1, 'day');

    // Format the date as DD/MM/YYYY.
    const formattedDate = nextMonday.format('DD/MM/YYYY');

    LStorage.setItem('HOLDING_DISCREPANCY_REMINDER', formattedDate);
  };
}

export default new HoldingsService();
